// Lumeros theme — single source of truth.
//
// Mirrors `Brand identity/BRAND.md` (Brand Identity v3, 20 May 2026).
//
// Brand thesis: "The brand is one wordmark and one full stop. Discipline is
// the system." Cream + ink do 95% of the work; Signal green appears only on
// the period and on one italicized word per headline.
//
// One factory `createVeraxisTheme(mode)` produces light (default, "cream")
// or dark ("ink"). Components import `useThemeMode()` from
// `./theme/ThemeModeContext`; raw colour tokens via `useThemeColors()`.

import { createTheme } from '@mui/material/styles';

// ── Brand-level constants (shared across modes) ──────────────────────────────
//
// Signal green is the single accent. Functional colours are deliberately
// muted so green stays reserved for the period and the italicized word.

export const SIGNAL       = '#1E9B49'; // The period · emphasis
export const SIGNAL_LIGHT = '#3BB76A';
export const SIGNAL_DARK  = '#16753A';
export const ACCENT       = SIGNAL;    // legacy alias used by some components
export const ACCENT_LIGHT = SIGNAL_LIGHT;
export const ACCENT_DARK  = SIGNAL_DARK;
export const SECONDARY    = SIGNAL_LIGHT;
export const SUCCESS      = SIGNAL;    // success uses signal too — single accent
export const WARN         = '#B58A2B'; // restrained ochre, used very sparingly
export const DANGER       = '#C8362E';

// Core brand neutrals
export const INK     = '#0E0F0C';
export const CREAM   = '#F4F2EC';
export const PAPER_2 = '#FAF8F2';
export const SURFACE = '#EBE9E2';
export const STONE   = '#6E6E64';

// Backwards-compat aliases for any old callers (do not use in new code)
export const PAPER  = CREAM;
export const INK_2  = '#1F201D';

// ── Per-mode palette ramps ───────────────────────────────────────────────────

// Light = the brand "cream" system: the default.
const LIGHT = {
  canvas:    CREAM,
  surface:   PAPER_2,
  surface2:  SURFACE,
  hairline:  'rgba(14,15,12,0.10)',
  hairline2: 'rgba(14,15,12,0.18)',
  text:      INK,
  textDim:   STONE,
  textMute:  'rgba(14,15,12,0.45)',
  hover:     'rgba(14,15,12,0.04)',
  hoverHi:   'rgba(14,15,12,0.06)',
  track:     'rgba(14,15,12,0.06)',
  trackHi:   'rgba(14,15,12,0.12)',
  skeleton:  'rgba(14,15,12,0.06)',
  bodyGradient: 'none', // brand discipline: no decorative gradients on cream
};

// Dark = the brand "ink" system: ink ground, ink-2 elevated surfaces, cream
// type. Same single signal accent.
const DARK = {
  canvas:    INK,
  surface:   '#17181513',
  surface2:  '#22231F',
  hairline:  'rgba(244,242,236,0.10)',
  hairline2: 'rgba(244,242,236,0.18)',
  text:      CREAM,
  textDim:   'rgba(244,242,236,0.55)',
  textMute:  'rgba(244,242,236,0.40)',
  hover:     'rgba(244,242,236,0.04)',
  hoverHi:   'rgba(244,242,236,0.06)',
  track:     'rgba(244,242,236,0.05)',
  trackHi:   'rgba(244,242,236,0.10)',
  skeleton:  'rgba(244,242,236,0.05)',
  bodyGradient: 'none',
};

// Dark surface fix: keep an actual hex (the literal above included an alpha
// suffix by accident). Use a clean ink-2 instead.
DARK.surface = '#17181A';

const RAMPS = { dark: DARK, light: LIGHT };

// ── Typography ──────────────────────────────────────────────────────────────
//
// Three voices. Serif leads.
//   - Instrument Serif: headlines · wordmark · pull quotes (display 400, italic)
//   - Inter Tight:      body · product · UI (sans 400/500/600)
//   - JetBrains Mono:   metadata · tags · data — never body
//
// Voice: short sentences ending in periods; italicize one word per headline.

const INSTRUMENT = '"Instrument Serif", Georgia, "Times New Roman", serif';
const INTER_TIGHT = '"Inter Tight", system-ui, -apple-system, "Segoe UI", sans-serif';
const JETBRAINS = '"JetBrains Mono", ui-monospace, "SF Mono", Menlo, monospace';

const typography = {
  fontFamily: INTER_TIGHT,
  fontFeatureSettings: '"tnum"',
  // Product headings stay in Inter Tight for legibility; the editorial
  // Instrument Serif is opted into via the `display*` variants below.
  h1: { fontWeight: 600, letterSpacing: '-0.020em' },
  h2: { fontWeight: 600, letterSpacing: '-0.018em' },
  h3: { fontWeight: 600, letterSpacing: '-0.014em' },
  h4: { fontWeight: 600, letterSpacing: '-0.010em' },
  h5: { fontWeight: 600, letterSpacing: '-0.006em' },
  h6: { fontWeight: 600, letterSpacing: '-0.004em' },
  subtitle1: { fontWeight: 500 },
  body1: { letterSpacing: '0.002em' },
  body2: { letterSpacing: '0.004em' },
  // Mono metadata — JetBrains Mono, not uppercased (per brand "tags · data").
  overline: {
    fontFamily: JETBRAINS,
    fontWeight: 500,
    letterSpacing: '0.04em',
    textTransform: 'none',
    fontSize: '0.72rem',
  },
  button: { textTransform: 'none', fontWeight: 600, letterSpacing: '0.002em' },

  // Editorial display variants — opt in via <Typography variant="display1" />.
  // Per BRAND.md §3: Instrument Serif 400, italic, lowercase, lh ~1.05.
  display1: {
    fontFamily: INSTRUMENT,
    fontStyle: 'italic',
    fontWeight: 400,
    fontSize: '3.25rem',    // ≈ 52px (the H2 reference in the brand book)
    lineHeight: 1.05,
    letterSpacing: '-0.005em',
    textTransform: 'lowercase',
  },
  display2: {
    fontFamily: INSTRUMENT,
    fontStyle: 'italic',
    fontWeight: 400,
    fontSize: '2.25rem',
    lineHeight: 1.08,
    letterSpacing: '-0.004em',
    textTransform: 'lowercase',
  },
  // Mono meta label (JetBrains Mono, Stone) — for section eyebrows + tags.
  meta: {
    fontFamily: JETBRAINS,
    fontWeight: 500,
    fontSize: '0.72rem',
    letterSpacing: '0.04em',
    textTransform: 'none',
    color: STONE,
  },
};

// ── Component overrides parameterised by ramp ───────────────────────────────

const componentOverrides = (ramp, mode) => ({
  MuiCssBaseline: {
    styleOverrides: {
      body: {
        backgroundColor: ramp.canvas,
        backgroundImage: ramp.bodyGradient,
        backgroundAttachment: 'fixed',
        fontFeatureSettings: '"tnum"',
      },
      '*::-webkit-scrollbar': { width: 10, height: 10 },
      '*::-webkit-scrollbar-thumb': {
        backgroundColor: mode === 'dark' ? 'rgba(244,242,236,0.14)' : 'rgba(14,15,12,0.18)',
        borderRadius: 8,
      },
    },
  },
  MuiPaper: {
    defaultProps: { elevation: 0 },
    styleOverrides: {
      root: {
        backgroundColor: ramp.surface,
        border: `1px solid ${ramp.hairline}`,
        backgroundImage: 'none',
      },
    },
  },
  MuiCard: {
    defaultProps: { elevation: 0 },
    styleOverrides: {
      root: {
        backgroundColor: ramp.surface,
        border: `1px solid ${ramp.hairline}`,
        borderRadius: 6,            // brand discipline: tight radii, not pillowy
        backgroundImage: 'none',
      },
    },
  },
  MuiButton: {
    defaultProps: { disableElevation: true },
    styleOverrides: {
      root: { borderRadius: 6, paddingInline: 14 },
      containedPrimary: { color: CREAM }, // cream type on signal green
    },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      root: {
        borderRadius: 6,
        backgroundColor: ramp.surface2,
        '& fieldset': { borderColor: ramp.hairline },
        '&:hover fieldset': { borderColor: ramp.hairline2 },
        '&.Mui-focused fieldset': { borderColor: SIGNAL, borderWidth: 1 },
      },
    },
  },
  MuiTableCell: {
    styleOverrides: {
      root: {
        borderBottom: `1px solid ${ramp.hairline}`,
        color: ramp.text,
      },
      head: {
        fontFamily: JETBRAINS,
        color: STONE,
        fontWeight: 500,
        textTransform: 'none',
        letterSpacing: '0.04em',
        fontSize: '0.72rem',
      },
    },
  },
  MuiChip: {
    styleOverrides: {
      root: {
        fontFamily: JETBRAINS,
        fontWeight: 500,
        borderRadius: 4,
        height: 22,
        letterSpacing: '0.04em',
        textTransform: 'none',
        '& .MuiChip-label': { paddingLeft: 8, paddingRight: 8 },
      },
      outlined: { borderColor: ramp.hairline2 },
      sizeSmall: { height: 20, fontSize: '0.70rem' },
    },
  },
  MuiTab: {
    styleOverrides: {
      root: {
        textTransform: 'none', fontWeight: 600, minHeight: 40,
        color: ramp.textDim, '&.Mui-selected': { color: ramp.text },
      },
    },
  },
  MuiTabs: {
    styleOverrides: { indicator: { backgroundColor: SIGNAL, height: 2 } },
  },
  MuiTooltip: {
    styleOverrides: {
      tooltip: {
        backgroundColor: INK,
        color: CREAM,
        fontSize: '0.75rem',
        fontFamily: JETBRAINS,
        border: `1px solid ${ramp.hairline2}`,
      },
      arrow: { color: INK },
    },
  },
  MuiAppBar: {
    defaultProps: { elevation: 0 },
    styleOverrides: {
      root: {
        backgroundColor: mode === 'dark' ? 'rgba(14,15,12,0.85)' : 'rgba(244,242,236,0.85)',
        backdropFilter: 'saturate(140%) blur(10px)',
        borderBottom: `1px solid ${ramp.hairline}`,
        color: ramp.text,
      },
    },
  },
  MuiDrawer: {
    styleOverrides: {
      paper: {
        backgroundColor: ramp.canvas,        // sidebar reads as part of the ground
        borderRight: `1px solid ${ramp.hairline}`,
      },
    },
  },
});

// ── Factory ─────────────────────────────────────────────────────────────────

export function createVeraxisTheme(mode = 'light') {
  const ramp = RAMPS[mode] || RAMPS.light;
  return createTheme({
    palette: {
      mode,
      primary:   { main: SIGNAL, light: SIGNAL_LIGHT, dark: SIGNAL_DARK, contrastText: CREAM },
      secondary: { main: SECONDARY },
      success:   { main: SUCCESS },
      warning:   { main: WARN },
      error:     { main: DANGER },
      background:{ default: ramp.canvas, paper: ramp.surface },
      text:      { primary: ramp.text, secondary: ramp.textDim, disabled: ramp.textMute },
      divider:   ramp.hairline,
    },
    shape:      { borderRadius: 6 },
    typography,
    components: componentOverrides(ramp, mode),
  });
}

// ── Backwards-compat exports ────────────────────────────────────────────────

export const veraxisLight = createVeraxisTheme('light');
export const veraxisDark  = createVeraxisTheme('dark');

export const colors = {
  ACCENT, ACCENT_LIGHT, ACCENT_DARK, SECONDARY, SUCCESS, WARN, DANGER,
  SIGNAL, SIGNAL_LIGHT, SIGNAL_DARK,
  INK, INK_2, CREAM, PAPER, PAPER_2, SURFACE, STONE,
  ...LIGHT,
};

export const ramps = { dark: DARK, light: LIGHT };
export default veraxisLight;
